import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Document, Page } from 'react-pdf'
import 'lib/pdfWorker'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import mammoth from 'mammoth'
import { read, utils } from 'xlsx'

// Worker configuration lives in lib/pdfWorker, imported above.

interface DocumentPreviewProps {
	uri: string
	fileType: string
	fileName: string
}

const Spinner = () => (
	<div className="flex items-center justify-center h-full">
		<div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#0080fe]" />
	</div>
)

const ErrorFallback = ({ message, uri, fileName }: { message: string; uri: string; fileName: string }) => (
	<div className="flex flex-col items-center justify-center h-full gap-4 text-gray-500">
		<svg className="h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
		</svg>
		<p className="text-sm">{message}</p>
		<a
			href={uri}
			download={fileName}
			className="inline-flex items-center gap-1.5 rounded-lg bg-[#0080fe] px-4 py-2 text-sm font-medium text-white hover:bg-[#0066cc] transition-colors"
		>
			<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
			</svg>
			Download File
		</a>
	</div>
)

const PdfViewer = ({ uri }: { uri: string }) => {
	const [numPages, setNumPages] = useState(0)
	const [pageNumber, setPageNumber] = useState(1)
	const pageAreaRef = useRef<HTMLDivElement>(null)
	const [pageHeight, setPageHeight] = useState(0)

	useEffect(() => {
		if (!pageAreaRef.current) return
		const observer = new ResizeObserver(entries => {
			for (const entry of entries) {
				setPageHeight(entry.contentRect.height)
			}
		})
		observer.observe(pageAreaRef.current)
		return () => observer.disconnect()
	}, [])

	const onDocumentLoadSuccess = useCallback(({ numPages: total }: { numPages: number }) => {
		setNumPages(total)
		setPageNumber(1)
	}, [])

	return (
		<div className="h-full flex flex-col">
			{/* Pagination controls */}
			<div className="flex items-center justify-center gap-3 py-2 border-b border-gray-200 bg-gray-50 flex-shrink-0">
				<button
					onClick={() => setPageNumber(p => Math.max(1, p - 1))}
					disabled={pageNumber <= 1}
					className="px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
				>
					Prev
				</button>
				<span className="text-xs text-gray-600">
					Page {pageNumber} of {numPages || '...'}
				</span>
				<button
					onClick={() => setPageNumber(p => Math.min(numPages, p + 1))}
					disabled={pageNumber >= numPages}
					className="px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
				>
					Next
				</button>
			</div>

			{/* PDF page */}
			<div ref={pageAreaRef} className="flex-1 overflow-hidden flex justify-center items-center bg-gray-100">
				<Document
					file={uri}
					onLoadSuccess={onDocumentLoadSuccess}
					loading={<Spinner />}
					error={<p className="text-sm text-red-500">Failed to load PDF.</p>}
				>
					{pageHeight > 0 && (
						<Page
							pageNumber={pageNumber}
							height={pageHeight}
							renderTextLayer={true}
							renderAnnotationLayer={true}
						/>
					)}
				</Document>
			</div>
		</div>
	)
}

const DocxViewer = ({ uri }: { uri: string; fileName: string }) => {
	const [html, setHtml] = useState('')
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')

	useEffect(() => {
		const convert = async () => {
			try {
				const response = await fetch(uri)
				if (!response.ok) throw new Error('Failed to fetch document')
				const arrayBuffer = await response.arrayBuffer()
				const result = await mammoth.convertToHtml({ arrayBuffer })
				setHtml(result.value)
			} catch (err: any) {
				setError(err.message || 'Failed to load document')
			} finally {
				setLoading(false)
			}
		}
		convert()
	}, [uri])

	if (loading) return <Spinner />
	if (error) return <ErrorFallback message={error} uri={uri} fileName="" />

	return (
		<div
			className="h-full overflow-y-auto p-6 bg-white [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-3 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mb-2 [&_h3]:text-lg [&_h3]:font-medium [&_h3]:mb-2 [&_p]:mb-2 [&_p]:leading-relaxed [&_table]:w-full [&_table]:border-collapse [&_table]:mb-4 [&_td]:border [&_td]:border-gray-300 [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-gray-300 [&_th]:px-2 [&_th]:py-1 [&_th]:bg-gray-50 [&_th]:font-semibold [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-2 [&_li]:mb-1 [&_img]:max-w-full [&_img]:h-auto"
			dangerouslySetInnerHTML={{ __html: html }}
		/>
	)
}

const DocLegacyViewer = ({ uri }: { uri: string }) => {
	const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(uri)}`
	return (
		<div className="h-full flex flex-col">
			<iframe src={viewerUrl} className="w-full flex-1 border-0 rounded" title="Document Preview" />
			<p className="text-xs text-gray-400 text-center mt-2">Rendered via Microsoft Office Online</p>
		</div>
	)
}

const SpreadsheetViewer = ({ uri }: { uri: string; fileName: string }) => {
	const [sheets, setSheets] = useState<{ name: string; html: string }[]>([])
	const [activeSheet, setActiveSheet] = useState(0)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')

	useEffect(() => {
		const parse = async () => {
			try {
				const response = await fetch(uri)
				if (!response.ok) throw new Error('Failed to fetch spreadsheet')
				const arrayBuffer = await response.arrayBuffer()
				const workbook = read(arrayBuffer)
				const parsed = workbook.SheetNames.map(name => ({
					name,
					html: utils.sheet_to_html(workbook.Sheets[name]),
				}))
				setSheets(parsed)
			} catch (err: any) {
				setError(err.message || 'Failed to load spreadsheet')
			} finally {
				setLoading(false)
			}
		}
		parse()
	}, [uri])

	if (loading) return <Spinner />
	if (error) return <ErrorFallback message={error} uri={uri} fileName="" />
	if (sheets.length === 0) return <ErrorFallback message="No data found in spreadsheet" uri={uri} fileName="" />

	return (
		<div className="h-full flex flex-col">
			{sheets.length > 1 && (
				<div className="flex gap-0.5 border-b border-gray-200 bg-gray-50 px-2 pt-2 flex-shrink-0">
					{sheets.map((sheet, idx) => (
						<button
							key={sheet.name}
							onClick={() => setActiveSheet(idx)}
							className={[
								'px-3 py-1.5 text-xs font-medium rounded-t-md transition-colors',
								idx === activeSheet
									? 'bg-white text-[#0080fe] border border-b-white border-gray-200 -mb-px'
									: 'text-gray-500 hover:text-gray-700 hover:bg-gray-100',
							].join(' ')}
						>
							{sheet.name}
						</button>
					))}
				</div>
			)}
			<div
				className="flex-1 overflow-auto p-2 [&_table]:w-full [&_table]:border-collapse [&_table]:text-xs [&_td]:border [&_td]:border-gray-200 [&_td]:px-2 [&_td]:py-1 [&_td]:whitespace-nowrap [&_th]:border [&_th]:border-gray-200 [&_th]:px-2 [&_th]:py-1 [&_th]:bg-gray-50 [&_th]:font-semibold [&_th]:text-left"
				dangerouslySetInnerHTML={{ __html: sheets[activeSheet]?.html || '' }}
			/>
		</div>
	)
}

const TextViewer = ({ uri }: { uri: string; fileName: string }) => {
	const [text, setText] = useState('')
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')

	useEffect(() => {
		const load = async () => {
			try {
				const response = await fetch(uri)
				if (!response.ok) throw new Error('Failed to fetch file')
				const content = await response.text()
				setText(content)
			} catch (err: any) {
				setError(err.message || 'Failed to load file')
			} finally {
				setLoading(false)
			}
		}
		load()
	}, [uri])

	if (loading) return <Spinner />
	if (error) return <ErrorFallback message={error} uri={uri} fileName="" />

	return (
		<pre className="h-full overflow-auto whitespace-pre-wrap font-mono text-sm p-4 bg-gray-50 rounded text-gray-800">
			{text}
		</pre>
	)
}

const DocumentPreviewPopupCN = ({ uri, fileType, fileName }: DocumentPreviewProps) => {
	const type = (fileType || '').toLowerCase()

	const renderViewer = () => {
		switch (type) {
			case 'pdf':
				return <PdfViewer uri={uri} />
			case 'docx':
				return <DocxViewer uri={uri} fileName={fileName} />
			case 'doc':
				return <DocLegacyViewer uri={uri} />
			case 'xlsx':
			case 'xls':
			case 'csv':
				return <SpreadsheetViewer uri={uri} fileName={fileName} />
			case 'txt':
				return <TextViewer uri={uri} fileName={fileName} />
			default:
				return <ErrorFallback message={`Preview not available for .${type} files`} uri={uri} fileName={fileName} />
		}
	}

	return <div className="w-full h-[80vh]">{renderViewer()}</div>
}

export default DocumentPreviewPopupCN
